import { z } from "zod";
import { format, formatISO, parseISO } from "date-fns";

export const MAX_TRANSACTION_REFERENCES = 20;

/**
 * One transaction attached to a task-chat message.
 *
 * Snapshot fields are CLIENT-SUPPLIED AND UNVERIFIED — provided by the
 * browser at send time and NOT validated against server-side transaction
 * data. Never use them for business logic or financial computations.
 * Treat as a display-only hint that may be stale or fabricated.
 */
export const TransactionReferenceSchema = z.object({
    /** Task the transaction list belongs to */
    taskId: z.string().uuid(),
    transactionId: z.number().int().positive(),
    /** Counterparty / description snapshot captured at send time */
    counterpartyName: z.string().min(1).max(500),
    /**
     * Amount snapshot (display only).
     *
     * @security CLIENT-SUPPLIED AND UNVERIFIED
     */
    amount: z.number().finite(),
    /** ISO date (YYYY-MM-DD) snapshot of the transaction date */
    date: z.string().date(),
    /**
     * Optional human-readable chip label, e.g. "Uber · $42.18 · Mar 3".
     * Generated client-side for display; not required for persistence.
     */
    label: z.string().max(500).optional(),
});
export type TransactionReference = z.infer<typeof TransactionReferenceSchema>;

export function toTransactionReferenceDate(date: Date): string {
    return formatISO(date, { representation: "date" });
}

export function buildTransactionRefLabel(
    ref: Pick<TransactionReference, "counterpartyName" | "amount" | "date">,
): string {
    const amount = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(ref.amount);
    const date = format(parseISO(ref.date), "MMM d");
    return `${ref.counterpartyName} · ${amount} · ${date}`;
}
